import { useState, type CSSProperties } from 'react';
import { Alert, Button, Input, Select } from 'antd';
import { submitForm } from '@/api/dataroom';
import type { PromptField, Task, TaskStatus } from '@/api/types';
import { useI18n } from '@/i18n/useI18n';
import { useTaskText } from './taskText';

const NATIVE_DATE_STYLE: CSSProperties = {
  height: 44,
  width: '100%',
  padding: '0 12px',
  borderRadius: 8,
  border: '1px solid var(--ol-border)',
  background: 'var(--ol-bg-container)',
  color: 'var(--ol-text-primary)',
  font: 'inherit',
  fontSize: 15,
  colorScheme: 'light dark',
};

/**
 * FORM task: renders the server-defined `promptFields` as a simple, accessible form. The field shape
 * is a defensive superset (the contract left it open — flagged to Track A); an unknown `type` falls
 * back to a single-line text input, so an unexpected field never blocks the applicant.
 */
export function FormTask({
  task,
  onStatus,
}: {
  task: Task;
  onStatus: (status: TaskStatus) => void;
}) {
  const { t } = useI18n();
  const text = useTaskText(task);
  const fields = task.promptFields ?? [];
  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);

  if (task.status !== 'PENDING' || done) {
    return <Alert type="success" showIcon message={t('form.submitted')} />;
  }
  if (fields.length === 0) {
    return <Alert type="info" showIcon message={t('form.noFields')} />;
  }

  const set = (name: string, v: string) => setValues((prev) => ({ ...prev, [name]: v }));

  function validate(): boolean {
    const next: Record<string, string> = {};
    for (const f of fields) {
      if (f.required && !(values[f.name] ?? '').trim()) {
        next[f.name] = t('form.requiredError');
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function submit() {
    setTopError(null);
    if (!validate()) {
      setTopError(t('form.fixErrors'));
      return;
    }
    setBusy(true);
    try {
      const res = await submitForm(task.taskId, values);
      onStatus(res.status);
      setDone(true);
    } catch {
      setTopError(t('error.inline'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      {topError && (
        <Alert
          type="error"
          showIcon
          message={topError}
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setTopError(null)}
        />
      )}

      {fields.map((f, i) => {
        const section = sectionOf(f);
        const startsSection = section !== null && section !== sectionOf(fields[i - 1]);
        return (
          <div key={f.name}>
            {/* A heading opens each consecutive run of same-section fields (studio-authored
                groups); sectionless fields render exactly as they always have. */}
            {startsSection && <h3 className="dr-field-section">{section}</h3>}
            <FieldRow
              field={f}
              value={values[f.name] ?? ''}
              error={errors[f.name]}
              onChange={(v) => set(f.name, v)}
              dateStyle={NATIVE_DATE_STYLE}
              selectPlaceholder={t('form.selectPlaceholder')}
            />
          </div>
        );
      })}

      <Button type="primary" size="large" block loading={busy} onClick={submit}>
        {busy ? t('form.submitting') : text('submit', 'form.submit')}
      </Button>
    </div>
  );
}

/** The field's trimmed section name, or null (undefined field = list edge = no section). */
function sectionOf(field: PromptField | undefined): string | null {
  const s = field?.section;
  return typeof s === 'string' && s.trim() ? s.trim() : null;
}

function FieldRow({
  field,
  value,
  error,
  onChange,
  dateStyle,
  selectPlaceholder,
}: {
  field: PromptField;
  value: string;
  error?: string;
  onChange: (v: string) => void;
  dateStyle: CSSProperties;
  selectPlaceholder: string;
}) {
  const id = `f-${field.name}`;
  const describedBy = error ? `${id}-err` : field.helpText ? `${id}-help` : undefined;
  const invalid = Boolean(error);

  return (
    <div className="dr-field">
      <label className="dr-field__label" htmlFor={id}>
        {field.label}
        {field.required && (
          <span className="dr-field__req" aria-hidden="true">
            *
          </span>
        )}
      </label>

      {field.type === 'textarea' ? (
        <Input.TextArea
          id={id}
          rows={4}
          value={value}
          placeholder={field.placeholder}
          status={invalid ? 'error' : undefined}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : field.type === 'select' ? (
        <Select
          id={id}
          style={{ width: '100%' }}
          value={value || undefined}
          placeholder={field.placeholder ?? selectPlaceholder}
          status={invalid ? 'error' : undefined}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          onChange={(v) => onChange(String(v ?? ''))}
          options={(field.options ?? []).map((opt) => ({ value: opt, label: opt }))}
        />
      ) : field.type === 'date' ? (
        <input
          id={id}
          type="date"
          value={value}
          style={dateStyle}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <Input
          id={id}
          type={
            field.type === 'number'
              ? 'number'
              : field.type === 'tel'
                ? 'tel'
                : field.type === 'email'
                  ? 'email'
                  : 'text'
          }
          value={value}
          placeholder={field.placeholder}
          status={invalid ? 'error' : undefined}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      {field.helpText && !error && (
        <div id={`${id}-help`} className="dr-field__help">
          {field.helpText}
        </div>
      )}
      {error && (
        <div id={`${id}-err`} className="dr-field__err">
          {error}
        </div>
      )}
    </div>
  );
}
